import * as React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Container, Label, Navbar, NavbarBrand, Row } from 'reactstrap';
import { FaChevronDown, FaChevronUp, FaPrint } from 'react-icons/fa';
import Select from 'react-select';
import { PeopleContext } from 'src/contexts/PeopleContext';
import { IUser } from '../Users/UsersTable';
import { authAxios } from 'src/services/AxiosService';
import { IReportingFile, IReportingFileWithClientName } from '../Files/FileContext';
import { DomainContext, FILE_TYPE_DUTY_COUNSEL, STAGE_ASSIGNED_TO_LAWYER } from 'src/contexts/DomainContext';
import { IndividualsContext } from 'src/contexts/IndividualsContext';
import { IOffence } from '../Offences/OffenceTable';
import { formatDate } from 'src/formatters/DateTimeFormatter';
import { ReportContext } from './ReportContext';
import { Column, TableInstance, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import { useDefaultColumn } from 'src/hooks/ReactTableHooks';

export const ActiveFilesByLawyer = () => {
  const [selectedLawyer, setSelectedLawyer] = useState();
  const peopleContext = useContext(PeopleContext);
  const individualsContext = useContext(IndividualsContext);
  const domainContext = useContext(DomainContext);
  const reportContext = useContext(ReportContext);
  const [filteredFiles, setFilteredFiles] = useState<IReportingFileWithClientName[]>([]);
  const [fileCount, setFileCount] = useState<number>(0);
  const individuals = individualsContext.state.individuals;
  const [offences, setOffences] = useState<IOffence[]>([]);
  const allFiles = reportContext.allFiles;
  const applicationTypes = domainContext.applicationTypes;
  const fileTypes = domainContext.fileTypes;
  const circuitLocations = domainContext.circuitLocations;
  const reportGeneratedDate = reportContext.reportGeneratedDate;

  const selectedLawyerUser = useMemo(
    () => peopleContext.lawyers.find((u: IUser) => u.userId === selectedLawyer),
    [peopleContext.lawyers, selectedLawyer]
  );

  useEffect(() => {
    const css = `@page {
  @bottom-right {
    padding-right: 20px;
    font-size: 12px !important;
    content: attr(data-print-date);
  }
}`;

    const style = document.createElement('style');

    style.id = 'print-page-footer';
    style.innerHTML = css;

    document.head.appendChild(style);

    return () => {
      const element = document.getElementById('print-page-footer');
      if (element) {
        document.head.removeChild(element);
      }
    };
  }, []);

  useEffect(() => {
    authAxios.get('/api/offences/admin').then((response) => {
      setOffences(response.data);
    });
  }, []);

  useEffect(() => {
    if (selectedLawyer) {
      const reportingFiles = allFiles.filter(
        (f) =>
          f.sId === STAGE_ASSIGNED_TO_LAWYER && f.lId && f.lId === selectedLawyer && f.fTId !== FILE_TYPE_DUTY_COUNSEL
      );
      setFilteredFiles(
        reportingFiles.map((f: IReportingFile) => {
          const individual = individuals.find((i) => i.individualId === f.cId);
          return {
            ...f,
            cFName: individual ? individual.firstName : '',
            cLName: individual ? individual.lastName : '',
          } as IReportingFileWithClientName;
        })
      );
      setFileCount(reportingFiles.length);
      document.documentElement.setAttribute('data-print-date', 'Date Printed: ' + formatDate(reportGeneratedDate));
    } else {
      setFilteredFiles([]);
      setFileCount(0);
    }
  }, [selectedLawyer, allFiles, individuals, reportGeneratedDate]);

  const fileCountsArray = useMemo(() => {
    const mapTemp = new Map<string, number>();
    filteredFiles.forEach((f) => {
      const totalSoFar = mapTemp.get(f.appTypeId);
      const updatedTotal: number = totalSoFar ? totalSoFar + 1 : 1;
      mapTemp.set(f.appTypeId, updatedTotal);
    });
    return Array.from(mapTemp.entries());
  }, [filteredFiles]);

  const columns: Column<IReportingFileWithClientName>[] = useMemo(
    () => [
      {
        Header: 'File #',
        id: 'fileNumber',
        width: 90,
        disableFilters: true,
        accessor: (f: IReportingFile) => {
          return f.fNum + (f.fSeqNum > 1 ? '-' + f.fSeqNum : '');
        },
      },
      {
        Header: 'Client',
        id: 'clientId',
        disableFilters: true,
        accessor: (f: IReportingFileWithClientName) => {
          return `${f.cLName.toUpperCase()}, ${f.cFName}`;
        },
      },
      {
        Header: 'Application Type',
        id: 'applicationTypeId',
        disableFilters: true,
        accessor: (f: IReportingFileWithClientName) => {
          return applicationTypes.find((a) => a.applicationTypeId === f.appTypeId)?.name;
        },
      },
      {
        Header: 'File Type',
        id: 'fileTypeId',
        disableFilters: true,
        accessor: (f: IReportingFileWithClientName) => {
          return fileTypes.find((ft) => ft.fileTypeId === f.fTId)?.name;
        },
      },
      {
        Header: 'Charges',
        disableFilters: true,
        id: 'chrgs',
        accessor: (f: IReportingFileWithClientName) => {
          return offences
            .filter((o) => f.chrgs.map((c) => c.offenceId).includes(o.offenceId))
            .map((o) => o.number)
            .join(', ');
        },
      },
      {
        Header: 'Circuit',
        id: 'cirLocId',
        disableFilters: true,
        accessor: (f: IReportingFileWithClientName) => {
          return circuitLocations.find((c) => c.circuitLocationId === f.cirLocId)?.name;
        },
      },
      {
        Header: 'App Date',
        disableFilters: true,
        accessor: (f: IReportingFileWithClientName) => {
          return formatDate(new Date(f.appDate));
        },
      },
      {
        Header: 'Expiry Date',
        disableFilters: true,
        accessor: (f: IReportingFileWithClientName) => {
          return formatDate(new Date(f.eDate));
        },
      },
      {
        Header: 'Court File #',
        id: 'cfNum',
        disableFilters: true,
        accessor: (f: IReportingFileWithClientName) => {
          return f.cfNum;
        },
      },
      {
        Header: 'Law Office #',
        id: 'lawOfficeNumber',
        width: 90,
        disableFilters: true,
        accessor: (f: IReportingFileWithClientName) => {
          return f.lawOfficeNumber;
        },
      },
    ],
    [offences, applicationTypes, circuitLocations, fileTypes]
  );

  const data: IReportingFileWithClientName[] = useMemo(() => filteredFiles, [filteredFiles]);

  const defaultColumn = useDefaultColumn();

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setPageSize,
  }: TableInstance<IReportingFileWithClientName> = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        sortBy: [
          {
            id: 'fileNumber',
            desc: false,
          },
        ],
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  //set page size to number of entries so they can all be printed at once
  useEffect(() => {
    if (data.length > 0) {
      setPageSize(data.length);
    } else {
      setPageSize(1);
    }
  }, [data, setPageSize]);

  return (
    <div>
      <Navbar color={'light'} light={true} expand={'xs'} className={'border-bottom sticky-top'}>
        <Row className={'flex-fill'}>
          <Col md={3} xl={2}>
            <NavbarBrand>Active Reports By Lawyer</NavbarBrand>
          </Col>
          <Col className={'d-flex'}>
            <Button className={'ml-auto'} color={'warning'} onClick={() => window.print()}>
              Print <FaPrint />
            </Button>
          </Col>
        </Row>
      </Navbar>
      <Container className={'my-5 container--no-margin-print'}>
        <Card className={'no-print'}>
          <CardHeader>Select Lawyer</CardHeader>
          <CardBody>
            <div className={'flex align-items-center'}>
              <Label className={'flex second-label'} style={{ paddingRight: '13px' }}>
                Lawyer
              </Label>
              {/* peopleContext.lawyers is populated with the "staff" end point which filters to the office for Legal Assistants and to the user for Lawyers  */}
              <Select
                style={{ maxWidth: '240px' }}
                options={peopleContext.lawyers
                  .filter((e) => e.active)
                  .sort((a, b) => (a.lastName + a.firstName > b.lastName + b.firstName ? 1 : -1))}
                placeholder={'Select lawyer...'}
                value={selectedLawyerUser}
                onChange={(value: any) => {
                  setSelectedLawyer(value?.userId || '');
                }}
                getOptionValue={(option: IUser) => option.userId}
                getOptionLabel={(option: IUser) => option.lastName.toUpperCase() + ', ' + option.firstName}
                isClearable={true}
              />
            </div>
          </CardBody>
        </Card>
        <div className={'report mt-5'}>
          <header className={'report-header'}>
            <h2 id={'count-header'} className={'text-center'}>{`Active Files By Lawyer (${formatDate(
              reportGeneratedDate
            )})`}</h2>
          </header>
          {selectedLawyer && (
            <>
              <div className={'my-4 d-flex justify-content-center'}>
                <table className={'report1'} style={{ width: '300px' }}>
                  <tr>
                    <th className={'header'}>File Type</th>
                    <th className={'header'}>Total</th>
                  </tr>
                  {fileCountsArray.map((f) => (
                    <tr key={f[0]}>
                      <td className={'report-row'}>
                        {applicationTypes.find((a) => a.applicationTypeId === f[0])?.name}
                      </td>
                      <td className={'report-row'}>{f[1]}</td>
                    </tr>
                  ))}
                  <tfoot>
                    <tr key={'total'}>
                      <th className={'header'}>
                        Total Files for{' '}
                        {selectedLawyerUser?.lastName.toUpperCase() + ', ' + selectedLawyerUser?.firstName}
                      </th>
                      <th className={'header'}>{fileCount}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
        <div className={'report'} style={{ minHeight: '750px' }}>
          <table className={'table table-bordered report1'} {...getTableProps()}>
            <thead style={{ overflow: 'wrap', cursor: 'pointer' }}>
              {headerGroups.map((headerGroup: any, index: number) => (
                <tr key={`files-table-thead-tr-${index}`} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any) => (
                    <th
                      key={`files-table-thead-tr-${index}-${column.id}`}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      width={column.width}
                      className={'header'}
                      style={{ verticalAlign: 'middle' }}
                    >
                      {column.render('Header')}
                      <span>{column.isSorted && (column.isSortedDesc ? <FaChevronDown /> : <FaChevronUp />)}</span>
                      <div onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
                        {column.canFilter ? column.render('Filter') : null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row: any) => {
                prepareRow(row);

                return (
                  <tr key={`files-table-tr-${row.id}`} {...row.getRowProps()}>
                    {row.cells.map((cell: any) => {
                      return (
                        <td
                          key={`files-table-td-${cell.row.id}-${cell.column.id}`}
                          {...cell.getCellProps([{ className: 'report-row' }])}
                          style={{ verticalAlign: 'middle' }}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Container>
    </div>
  );
};
